export type SlackEmbedErrorCode =
  | 'session_expired'
  | 'no_access'
  | 'not_found'
  | 'unsupported_widget'
  | 'no_data'
  | 'error';

export type SlackEmbedMode = 'interactive' | 'render';

export type SlackEmbedExportPngOptions = { width?: number };

export type SlackEmbedExportPng = (options?: SlackEmbedExportPngOptions) => Promise<string>;

/**
 * Contract with the Slack chart renderer: it polls `ready` / `error` and then calls `exportPng`.
 */
export interface NeptuneEmbedApi {
  readonly version: 1;
  readonly mode: SlackEmbedMode;
  /** True once the widget rendered data; never true after an error. */
  ready: boolean;
  /** Terminal error, set at most once. */
  error: SlackEmbedErrorCode | null;
  /** Resolves with base64 PNG bytes (no "data:" prefix); rejects with Error(<code>) when not ready. */
  exportPng: SlackEmbedExportPng;
}

declare global {
  interface Window {
    __NEPTUNE_EMBED__?: NeptuneEmbedApi;
  }
}

const NOT_READY_ERROR = 'not_ready';

export function initNeptuneEmbedApi(mode: SlackEmbedMode): NeptuneEmbedApi {
  const api: NeptuneEmbedApi = {
    version: 1,
    mode,
    ready: false,
    error: null,
    exportPng: () => Promise.reject(new Error(api.error ?? NOT_READY_ERROR)),
  };

  window.__NEPTUNE_EMBED__ = api;

  return api;
}

export function getNeptuneEmbedApi(): NeptuneEmbedApi | undefined {
  return window.__NEPTUNE_EMBED__;
}

export function getNeptuneEmbedMode(): SlackEmbedMode | undefined {
  return getNeptuneEmbedApi()?.mode;
}

export function markNeptuneEmbedReady(exportPng: SlackEmbedExportPng): void {
  const api = getNeptuneEmbedApi();

  if (!api || api.error !== null) {
    return;
  }

  api.exportPng = (options) => {
    if (api.error !== null || !api.ready) {
      return Promise.reject(new Error(api.error ?? NOT_READY_ERROR));
    }

    return exportPng(options);
  };

  api.ready = true;
}

/**
 * Records a terminal error. Only the first error is kept; returns the error that is in effect.
 */
export function markNeptuneEmbedError(code: SlackEmbedErrorCode): SlackEmbedErrorCode {
  const api = getNeptuneEmbedApi();

  if (!api) {
    return code;
  }

  api.ready = false;

  if (api.error === null) {
    api.error = code;
  }

  return api.error;
}
